import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import {
  friendlyDismissError,
  friendlyHopError,
  friendlyPopupError,
  openPromoHop,
  promoCard,
  SUCCEED_URL,
  accountIsChurch,
  visiblePromos,
} from '@/lib/ownerPromo';

const TONE = {
  operate: {
    card: 'border-indigo-500/40 bg-gradient-to-r from-indigo-500/15 via-slate-900/80 to-slate-900/40 shadow-lg shadow-indigo-500/10',
    rule: 'bg-indigo-400',
    kicker: 'text-indigo-300',
    button: 'bg-indigo-500 hover:bg-indigo-600 text-white shadow-md shadow-indigo-500/20',
  },
  leads: {
    card: 'border-emerald-500/40 bg-gradient-to-r from-emerald-500/15 via-slate-900/80 to-slate-900/40 shadow-lg shadow-emerald-500/10',
    rule: 'bg-emerald-400',
    kicker: 'text-emerald-300',
    button: 'bg-emerald-500 hover:bg-emerald-400 text-slate-950 shadow-md shadow-emerald-500/20',
  },
  laser: {
    card: 'border-amber-500/40 bg-gradient-to-r from-amber-500/15 via-slate-900/80 to-slate-900/40 shadow-lg shadow-amber-500/10',
    rule: 'bg-amber-400',
    kicker: 'text-amber-300',
    button: 'bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-md shadow-amber-500/20',
  },
};

export default function OwnerPromoBanner({ dashboard }) {
  const [promos, setPromos] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.getClientPromos()
      .then((res) => {
        if (!cancelled) setPromos(Array.isArray(res?.data?.promos) ? res.data.promos : []);
      })
      .catch(() => {
        if (!cancelled) setPromos([]);
      });
    return () => { cancelled = true; };
  }, []);

  if (promos === null) return null;
  const keys = visiblePromos(promos, dashboard);
  const church = accountIsChurch(dashboard);
  return (
    <div data-testid="owner-promo-list">
      {keys.map((key) => <PromoCard key={key} promoKey={key} />)}
      {!church && (
        <p className="-mt-3 mb-[24px] text-sm text-slate-400" data-testid="owner-promo-succeed">
          Want more customers?{' '}
          <a href={SUCCEED_URL} target="_blank" rel="noreferrer" className="font-medium text-indigo-300 hover:text-indigo-200">
            See the Succeed marketing packs
          </a>
          .
        </p>
      )}
    </div>
  );
}

function PromoCard({ promoKey }) {
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const hopping = useRef(false);
  const key = hidden ? '' : promoKey;
  const card = promoCard(key);
  if (!card) return null;
  const tone = TONE[key] || TONE.operate;

  const dismiss = () => {
    const current = key;
    // Hide immediately in this view. The server keeps the dismissal: the first
    // Not now is 90 days, then 180, then it does not return. The next load,
    // including a new session, asks GET /client/promos and stays blank while
    // that window holds. Nothing is stored in the browser.
    setHidden(true);
    setError('');
    api.dismissClientPromo(current).catch(() => {
      setHidden(false);
      setError(friendlyDismissError());
    });
  };

  const hop = () => {
    if (hopping.current) return;
    hopping.current = true;
    setBusy(true);
    setError('');
    const current = key;
    openPromoHop(() => api.hopClientPromo(current).then((res) => res?.data?.url))
      .catch((err) => {
        if (err?.code === 'popup_blocked') setError(friendlyPopupError());
        else if (err?.code === 'no_url') setError(friendlyHopError(0));
        else setError(friendlyHopError(err?.response?.status));
      })
      .finally(() => {
        hopping.current = false;
        setBusy(false);
      });
  };

  return (
    <section
      className={`relative mb-[24px] overflow-hidden rounded-xl border ${tone.card}`}
      data-testid="owner-promo-banner"
      data-promo-key={key}
    >
      <div className={`absolute inset-y-0 left-0 w-1 ${tone.rule}`} aria-hidden="true" />
      <div className="flex flex-col gap-3 px-4 py-3 pl-5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5 sm:py-3.5 sm:pl-6">
        <div className="min-w-0">
          <p className={`text-[11px] font-semibold uppercase tracking-wide ${tone.kicker}`}>{card.kicker}</p>
          <h3 className="mt-0.5 text-base font-semibold text-white leading-snug" style={{ fontFamily: 'Outfit' }}>{card.title}</h3>
          <p className="mt-1 text-sm text-slate-300 leading-snug">{card.reason}</p>
          {error && (
            <p className="mt-2 text-sm text-amber-200" role="alert" data-testid="owner-promo-error">{error}</p>
          )}
        </div>
        <div className="flex w-full shrink-0 flex-col items-center gap-1 sm:w-auto sm:flex-row sm:items-center sm:gap-2">
          <button
            type="button"
            onClick={hop}
            disabled={busy}
            className={`inline-flex min-h-[44px] w-full items-center justify-center rounded-lg px-4 text-sm font-medium transition-all active:scale-[0.98] disabled:opacity-60 sm:w-auto ${tone.button}`}
            data-testid="owner-promo-hop"
          >
            {busy ? 'Opening…' : card.action}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center border-0 bg-transparent px-3 text-sm font-medium text-slate-300 transition-colors hover:text-white sm:rounded-lg sm:border sm:border-slate-600 sm:bg-slate-900/60 sm:px-4 sm:text-slate-200 sm:hover:border-slate-400"
            data-testid="owner-promo-dismiss"
          >
            Not now
          </button>
        </div>
      </div>
    </section>
  );
}
